import "./SurveyTakingPreview.scss";

import { Flex, FlexItem } from "@fluentui/react-northstar";
import { createSurveyCard, getCardPageSize, getSurveyFilteredReminders } from "@teams/cards";
import { TeamsApplicationTypes, TeamsSurveyDraft, TeamsSurveyTypes, TeamsUser } from "@teams/types";
import moment from "moment";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ReactHTMLElement } from "../../../../../../_shared/components/ReactHTMLElement";
import { useButton, useDefaultResponseValues, useTheme } from "../../hooks";
import { Header } from "./Header";
import { surveyDetailsToSurvey } from "./utils";

type Props = {
  draft: TeamsSurveyDraft;
  owner: TeamsUser;
  applicationType: TeamsApplicationTypes;
  tenantId: string;
  closePopupHandler: () => void;
};

export const SurveyTakingPreview: React.FC<Props> = props => {
  const ref = useRef<HTMLDivElement>();

  const survey = useMemo(() => {
    const draft = props.draft;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id, ownerId, schedule, ...surveyDetails } = {
      ...draft,
      recipients: draft.recipients || [],
      closeAt:
        !draft.closeAt && draft.openDuration && draft.deliverAt
          ? moment(draft.deliverAt).add(moment.duration(draft.openDuration)).toDate()
          : draft.closeAt,
      reminders: getSurveyFilteredReminders(draft.reminders),
      subscriptionId: undefined as unknown as string,
    };

    const survey = {
      ...surveyDetailsToSurvey(surveyDetails, [props.owner], props.tenantId, props.applicationType),
      state: "open" as const,
    };

    return survey;
  }, [props.applicationType, props.draft, props.owner, props.tenantId]);

  const { pageSize, pageNo, questionIndexId, nextPage, prevPage } = usePagination(
    props.draft.type,
    survey.questions
  );

  const generateCardHtml = useCallback(() => {
    const html = createSurveyCard(survey, {
      showHeader: true,
      maxQuestionDisplay: pageSize,
      questionIndexId: questionIndexId,
    }).render()!;

    return html;
  }, [pageSize, questionIndexId, survey]);

  const [cardHtml, setCardHtml] = useState<HTMLElement>(() => generateCardHtml());

  useTheme(cardHtml);
  useDefaultResponseValues(cardHtml);

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [pageNo]);

  useEffect(() => {
    setCardHtml(generateCardHtml());
  }, [generateCardHtml]);

  const { attachClickHandler, buttonLabels } = useButton(cardHtml);

  useEffect(() => {
    attachClickHandler(buttonLabels.NextButtonLabel, nextPage);
    attachClickHandler(buttonLabels.PreviousButtonLabel, prevPage);
  }, [
    attachClickHandler,
    nextPage,
    prevPage,
    buttonLabels.NextButtonLabel,
    buttonLabels.PreviousButtonLabel,
  ]);

  useEffect(() => {
    attachClickHandler(buttonLabels.SubmitResponseButtonLabel, props.closePopupHandler);
  }, [attachClickHandler, buttonLabels.SubmitResponseButtonLabel, props]);

  return (
    <Flex column className="multi-question-popup-preview">
      <FlexItem>
        <Header title={props.draft.title} />
      </FlexItem>
      <FlexItem>
        <div ref={ref as RefObject<HTMLDivElement>}>
          <ReactHTMLElement html={cardHtml} />
        </div>
      </FlexItem>
    </Flex>
  );
};

function usePagination(surveyType: TeamsSurveyTypes, questions: Array<{ id: string }>) {
  const [pageNo, setPageNo] = useState(0);

  const pageSize = getCardPageSize({
    type: surveyType,
  });

  useEffect(() => {
    const questionsLength = questions.length;
    let currentQuestionIndex = pageSize * pageNo;
    if (currentQuestionIndex >= questionsLength) {
      setPageNo(pageNo - 1);
    }
  }, [pageNo, pageSize, questions.length]);

  return {
    pageSize,
    pageNo,
    questionIndexId: questions[pageSize * pageNo]?.id || questions[0]?.id,
    nextPage: () => setPageNo(pageNo + 1),
    prevPage: () => setPageNo(pageNo - 1),
  };
}
